import {
  CashAccountLiteModel,
  CashAccountModel,
  getCashAccountKey,
  getCashAccountsKey,
  useGetCashAccount,
  useGetCashAccounts,
} from 'api-hooks/cash-account';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(item: CashAccountLiteModel | CashAccountModel): Option {
  return {
    id: item.id,
    label: transformLabel([item.code, item.name], ' - '),
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: CashAccountModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
  showCreate?: boolean;
}

export default function CashAccountSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:cash_account'),
    placeholder = t('common:select_cash_account'),
    showCreate = true,
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const permission = useGetCrudPermission('cash-accounts');
  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetCashAccounts,
    useDetailLazyQueryHook: useGetCashAccount,
    listKeyFunction: getCashAccountsKey,
    detailKeyFunction: getCashAccountKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
        },
      }),
      [],
    ),
    renderCreate: showCreate && permission.create,
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.CashAccountsCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, CashAccountModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      overrides={overrides}
      {...selectProps}
    />
  );
}
